import React, { type ReactNode } from 'react';
import { Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';

import { goBackFromAuth } from '../../../core/navigation/navigationRef';
import { radii, spacing } from '../../../core/theme/tokens';

type AuthModalFrameProps = {
  children: ReactNode;
  onDismiss?: () => void;
  style?: StyleProp<ViewStyle>;
};

/**
 * Web auth: centered dialog over a light transparent dim so the app stays visible.
 * (Desktop pattern — not a mobile bottom sheet.)
 */
export function AuthModalFrame({
  children,
  onDismiss = goBackFromAuth,
  style,
}: AuthModalFrameProps) {
  return (
    <View style={styles.overlay}>
      <Pressable
        style={styles.backdrop}
        onPress={onDismiss}
        accessibilityRole="button"
        accessibilityLabel="Dismiss"
      />
      <View style={[styles.dialog, style]}>
        <View style={styles.glass}>{children}</View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
    backgroundColor: 'transparent',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    // Very light dim — MainTabs stays mounted under the overlay
    backgroundColor: 'rgba(2, 6, 23, 0.18)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
  } as ViewStyle,
  dialog: {
    zIndex: 2,
    width: '100%',
    maxWidth: 400,
    animationKeyframes: {
      '0%': { opacity: 0, transform: 'translateY(10px) scale(0.97)' },
      '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
    animationDuration: '280ms',
    animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
    animationFillMode: 'both',
  } as ViewStyle,
  glass: {
    borderRadius: radii.xl + 4,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.16)',
    backgroundColor: 'rgba(15, 22, 32, 0.82)',
    backdropFilter: 'blur(24px) saturate(170%)',
    WebkitBackdropFilter: 'blur(24px) saturate(170%)',
    boxShadow:
      '0 24px 64px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(61, 214, 164, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.12)',
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
    gap: spacing.lg,
  } as object,
});
